import { useCallback, useEffect, useRef, useState } from "react";
import { useUpdateParticipationRequestNotesMutation } from "../../../../autogen/bff-api";
import { notifyChangesSaved, UnsavedChangesError } from "../../../../common/editing/save-coordinator";
import type { SaveStatus } from "../../../../common/editing/SaveStatusBadge/SaveStatusBadge";
import { useApiError } from "../../../../common/errors/useApiError";

const DEBOUNCE_MS = 300;

interface FieldState {
  value: string;
  status: SaveStatus;
}

type EditorValues = {
  internalNote: string;
  rejectionReason: string;
  exclusionGroundsAssessment: string;
};

type FieldKey = keyof EditorValues;

const FIELDS: FieldKey[] = ["internalNote", "rejectionReason", "exclusionGroundsAssessment"];

export interface UseBuyerReviewNotesEditor {
  values: EditorValues;
  setField: (field: FieldKey, value: string) => void;
  statusFor: (field: FieldKey) => SaveStatus;
  flush: () => Promise<void>;
}

type ServerNotes = Record<FieldKey, string | null | undefined>;

// Mirrors the pending-map approach of useRequestQualificationReviewEditor but tracks per-field
// save status so the UI can show individual persistence indicators. The pending map always holds
// the latest unsaved value; a save that completes while a newer value is pending triggers a
// follow-up save rather than dropping the keystrokes.
export const useBuyerReviewNotesEditor = (
  eventId: string,
  requestId: string,
  serverNotes: ServerNotes,
): UseBuyerReviewNotesEditor => {
  const { trigger } = useApiError();
  const [updateNotes] = useUpdateParticipationRequestNotesMutation();
  const isMounted = useRef(true);

  const [fields, setFields] = useState<Record<FieldKey, FieldState>>(() => ({
    internalNote: { value: serverNotes.internalNote ?? "", status: "idle" },
    rejectionReason: { value: serverNotes.rejectionReason ?? "", status: "idle" },
    exclusionGroundsAssessment: { value: serverNotes.exclusionGroundsAssessment ?? "", status: "idle" },
  }));

  // Latest unsaved value per field — only removed when that exact value is being sent.
  const pendingValues = useRef<Partial<EditorValues>>({});
  // Active debounce timers per field.
  const flushTimers = useRef<Partial<Record<FieldKey, ReturnType<typeof setTimeout>>>>({});
  // In-flight save promises keyed by field — so flush() and follow-up saves can await them.
  const inFlightPromises = useRef<Partial<Record<FieldKey, Promise<boolean>>>>({});
  const flushWaiters = useRef(0);

  useEffect(() => {
    // Per-field guard: only sync a field from the server when that specific field has no
    // pending or in-flight save. This way a concurrent internalNote edit does not suppress
    // a server-pushed rejectionReason update (and vice versa).
    setFields((prev) => {
      let next = prev;
      for (const field of FIELDS) {
        const hasPending = pendingValues.current[field] !== undefined;
        const hasInFlight = inFlightPromises.current[field] !== undefined;
        if (hasPending || hasInFlight) continue;
        const normalized = serverNotes[field] ?? "";
        if (prev[field].value !== normalized || prev[field].status !== "idle") {
          next = { ...next, [field]: { value: normalized, status: "idle" } };
        }
      }
      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serverNotes.internalNote, serverNotes.rejectionReason, serverNotes.exclusionGroundsAssessment]);

  const saveFieldRef = useRef<(field: FieldKey) => Promise<boolean>>(() => Promise.resolve(true));
  const saveField = useCallback(
    (field: FieldKey): Promise<boolean> => {
      const existing = inFlightPromises.current[field];
      if (existing) {
        return existing.then((saved) => (saved ? saveFieldRef.current(field) : false));
      }

      const value = pendingValues.current[field];
      if (value === undefined) return Promise.resolve(true);
      delete pendingValues.current[field];
      let failed = false;

      if (isMounted.current) {
        setFields((prev) => ({ ...prev, [field]: { ...prev[field], status: "saving" } }));
      }

      const promise: Promise<boolean> = updateNotes({
        eventId,
        requestId,
        buyerReviewNotesPatchDto: { [field]: value },
      })
        .unwrap()
        .then(() => {
          if (!isMounted.current) return true;
          setFields((prev) =>
            // Don't clobber a newer "pending" status from keystrokes typed during the save.
            prev[field].status === "saving"
              ? { ...prev, [field]: { ...prev[field], status: "saved" } }
              : prev,
          );
          return true;
        })
        .catch((e: unknown) => {
          failed = true;
          if (pendingValues.current[field] === undefined) pendingValues.current[field] = value;
          if (!isMounted.current) return false;
          setFields((prev) =>
            prev[field].status === "saving"
              ? { ...prev, [field]: { ...prev[field], status: "error" } }
              : prev,
          );
          if (flushWaiters.current === 0) trigger(e);
          return false;
        })
        .finally(() => {
          delete inFlightPromises.current[field];
          const typedDuringSave = pendingValues.current[field] !== undefined && pendingValues.current[field] !== value;
          if (typedDuringSave || (!failed && pendingValues.current[field] !== undefined)) {
            void saveFieldRef.current(field);
          }
        });

      inFlightPromises.current[field] = promise;
      return promise;
    },
    [eventId, requestId, trigger, updateNotes],
  );
  saveFieldRef.current = saveField;

  const setField = useCallback((field: FieldKey, value: string) => {
    setFields((prev) => ({ ...prev, [field]: { value, status: "pending" } }));
    pendingValues.current[field] = value;
    if (flushTimers.current[field]) clearTimeout(flushTimers.current[field]);
    flushTimers.current[field] = setTimeout(() => {
      delete flushTimers.current[field];
      void saveFieldRef.current(field);
    }, DEBOUNCE_MS);
  }, []);

  const saveAllNow = useCallback(async () => {
    for (const field of FIELDS) {
      const timer = flushTimers.current[field];
      if (timer) {
        clearTimeout(timer);
        delete flushTimers.current[field];
      }
    }
    await Promise.all(FIELDS.map((field) => saveField(field)));
  }, [saveField]);

  const flush = useCallback(async () => {
    flushWaiters.current++;
    try {
      await saveAllNow();
    } finally {
      flushWaiters.current--;
    }
    if (FIELDS.some((field) => pendingValues.current[field] !== undefined)) throw new UnsavedChangesError();
    notifyChangesSaved();
  }, [saveAllNow]);

  const saveAllNowRef = useRef(saveAllNow);
  saveAllNowRef.current = saveAllNow;
  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
      void saveAllNowRef.current();
    };
  }, []);

  const statusFor = useCallback(
    (field: FieldKey): SaveStatus => fields[field].status,
    [fields],
  );

  return {
    values: {
      internalNote: fields.internalNote.value,
      rejectionReason: fields.rejectionReason.value,
      exclusionGroundsAssessment: fields.exclusionGroundsAssessment.value,
    },
    setField,
    statusFor,
    flush,
  };
};
